import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { AdRenderer, type AdRenderModel, type AdFormat } from "@/components/ads/AdRenderer";

type FeedAd = AdRenderModel & { ctaType: string; ctaValue: string; ctaSlug?: string | null };

// One fetch per placement+town, shared by every slot on the page. Slots pick a
// different ad by `index` so a feed with several woven slots rotates through them.
const cache = new Map<string, { ads: FeedAd[]; ts: number }>();
const inflight = new Map<string, Promise<FeedAd[]>>();
const TTL = 60_000;

// Per-placement rotation so the LEADING ad isn't pinned to ads[0] every view.
// The cached list is one random server shuffle (held ~60s); without this, slot
// index 0 always rendered ads[0] — i.e. the same advert first every time. We
// start at a random offset and advance it on each slot mount, so the first slot
// cycles through all ads across views while sibling slots stay distinct.
const rotor = new Map<string, number>();
const lastServed = new Map<string, number>(); // last ad id shown for this placement — never repeat it back-to-back
function pickAd(key: string, ads: FeedAd[], index: number): FeedAd | null {
  const len = ads.length;
  if (len === 0) return null;
  const cur = rotor.get(key);
  const base = cur == null ? Math.floor(Math.random() * len) : cur;
  rotor.set(key, base + 1);
  let chosen = ads[(index + base) % len]!;
  // Guarantee no two slots in a row show the same advert.
  if (len > 1 && lastServed.get(key) === chosen.id) chosen = ads[(index + base + 1) % len]!;
  lastServed.set(key, chosen.id);
  return chosen;
}
function loadAds(placement: string, town?: string | null): Promise<FeedAd[]> {
  const key = `${placement}|${town ?? ""}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.ts < TTL) return Promise.resolve(hit.ads);
  const existing = inflight.get(key);
  if (existing) return existing;
  const q = new URLSearchParams({ placement });
  if (town) q.set("town", town);
  const p = fetch(`/api/ads/feed?${q.toString()}`, { cache: "no-store" })
    .then(r => r.ok ? r.json() : { ads: [] })
    .then(d => { const ads = (d.ads ?? []) as FeedAd[]; cache.set(key, { ads, ts: Date.now() }); inflight.delete(key); return ads; })
    .catch(() => { inflight.delete(key); return [] as FeedAd[]; });
  inflight.set(key, p);
  return p;
}

/**
 * In-feed advert slot. Drop `<FeedAdSlot placement="deals" town={town} index={n} />`
 * at intervals through a browse feed. Each index shows a different rotating ad, fires
 * impression (on view) + click (on CTA). When no paid ad exists, index 0 shows a house
 * "Advertise here" prompt; further indexes render nothing.
 */
export default function FeedAdSlot({ placement, town, index = 0, forceFormat, hideHouse }: { placement: string; town?: string | null; index?: number; forceFormat?: AdFormat; hideHouse?: boolean }) {
  const [ad, setAd] = useState<FeedAd | null>(null);
  const [empty, setEmpty] = useState(false);
  const [, navigate] = useLocation();
  const seen = useRef(false);

  useEffect(() => {
    let alive = true;
    loadAds(placement, town).then(ads => {
      if (!alive) return;
      if (ads.length === 0) { setEmpty(true); setAd(null); return; }
      setEmpty(false);
      setAd(pickAd(`${placement}|${town ?? ""}`, ads, index));
    });
    return () => { alive = false; };
  }, [placement, town, index]);

  useEffect(() => {
    if (ad && !seen.current) { seen.current = true; fetch(`/api/ads/${ad.id}/impression`, { method: "POST" }).catch(() => {}); }
  }, [ad]);

  function onClick(a: FeedAd) {
    fetch(`/api/ads/${a.id}/click`, { method: "POST" }).catch(() => {});
    switch (a.ctaType) {
      case "url": window.open(a.ctaValue.startsWith("http") ? a.ctaValue : `https://${a.ctaValue}`, "_blank", "noopener"); break;
      case "phone": window.location.href = `tel:${a.ctaValue}`; break;
      case "sms": {
        const body = `Hi, saw your advert${a.businessName ? ` for ${a.businessName}` : ""} on Ayrshire Hub - `;
        // `?&body=` works across iOS and Android SMS handlers.
        window.location.href = `sms:${a.ctaValue}?&body=${encodeURIComponent(body)}`;
        break;
      }
      case "store": navigate(`/order/${a.ctaSlug ?? a.ctaValue}`); break;
      case "deal": navigate(`/deals/${a.ctaValue}`); break;
      case "event": navigate(`/event/${a.ctaValue}`); break;
    }
  }

  if (ad) return <AdRenderer ad={forceFormat ? { ...ad, format: forceFormat } : ad} onClick={() => onClick(ad)} />;

  // House prompt only for the first slot when there are no paid ads at all.
  if (empty && index === 0 && !hideHouse) {
    return (
      <button onClick={() => navigate("/advertise")}
        className="w-full rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-left hover:bg-slate-100 transition flex items-center gap-3">
        <span className="text-2xl">📣</span>
        <span className="flex-1">
          <span className="block font-semibold text-primary">Advertise your business here</span>
          <span className="block text-sm text-slate-600">Reach thousands of locals across Ayrshire - from just a few pounds a week.</span>
        </span>
        <span className="text-primary text-sm font-medium shrink-0">Get started →</span>
      </button>
    );
  }
  return null;
}
